import { useWriteValidationError } from "@/shared/lib/errors/err-validation-failed-context";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Lock } from "lucide-react";
import { useEffect, useState } from "react";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";
import t from "@/lang/lang";
import { WorkOrder } from "@/services/backend/rlt/work-orders/types/work-order";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useRltWorkOrderUpdateStateMutation } from "@/services/backend/rlt/work-orders/api/work-order-api";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { DocumentState } from "@/services/backend/documents/types/state";

export function SetWorkOrderImmutableDialog({
  workOrder,
}: {
  workOrder: WorkOrder;
}) {
  const [open, setOpen] = useState(false);
  const [patch, { isLoading, error, isSuccess, reset }] =
    useRltWorkOrderUpdateStateMutation();
  const { user } = useAuth();

  useEffect(() => {
    if (isSuccess) {
      reset();
      setOpen(false);
    }
  }, [isSuccess, reset]);

  // A work order which is not ready for the customer names the fields it is
  // missing. They belong on the page behind this dialog, where the user
  // resolves them; the alert below keeps showing the message.
  useWriteValidationError(error);

  const setImmutable = () => {
    if (isLoading) return;
    patch({
      id: workOrder.id,
      state: DocumentState.Immutable,
      stateSetBy: user?.id || "",
    });
  };

  if (isImmutable(workOrder)) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Lock className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader
          title={t("Arbeitsschein festschreiben?")}
          description={t(
            "Der Arbeitsschein wir festgeschrieben. Er kann danach nicht mehr geändert werden. Diese Aktion kann nicht rückgängig gemacht werden.",
          )}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={setImmutable}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
